/* eslint-disable no-plusplus */
import { range } from "lodash";
import {
  nonEditable,
  textCell,
  headerCell,
} from "../cells";
import { HEADER_ROW_ID, ROW_HEIGHT } from "../constants";
import { formatCurrency } from "@/utils/helpers";
import { getGroupRows, sumGroup } from "../tenant-statement/getTenantStatementRows";

function getHeaderRow(columns) {
  return {
    rowId: HEADER_ROW_ID,
    height: ROW_HEIGHT,
    cells: columns.map((column) => headerCell(column.label)),
  };
}

function getOpenTransactionGroupRows(title, groups, columns, total, type, totalAging) {
  const totalObjects = type === "open" ? [
    {
      rowId: `${title}-Footer`,
      height: ROW_HEIGHT,
      cells: [
        headerCell(title, "total-row"),
        ...range(0, columns.length - 5).map(() => headerCell("", "total-row")),
        headerCell(
          "Total", "total-row-debit"),
        headerCell(formatCurrency(total?.totalOriginalAmount || 0), "total-row-credit"),
        headerCell(formatCurrency(total?.totalPaidAmount || 0), "total-row-credit"),
        headerCell(formatCurrency(total?.totalBalance || 0), "total-row-credit"),
      ],
    },
    {
      rowId: `${title}-Footer`,
      height: ROW_HEIGHT,
      cells: [
        headerCell(title, "total-row"),
        ...range(0, columns.length - 3).map(() => headerCell("", "total-row")),
        headerCell(
          "Net to be Collected", "total-row-debit"),
        headerCell(formatCurrency(total?.totalBalance || 0), "total-row-credit"),
      ],
    },
  ] : [
    {
      rowId: `${title}-Footer`,
      height: ROW_HEIGHT,
      cells: [
        headerCell(
          "Total", "total-row-debit"),
        headerCell(formatCurrency(total?.totalNotDue || 0), "total-row-credit"),
        headerCell(formatCurrency(total?.totalOneMonth || 0), "total-row-credit"),
        headerCell(formatCurrency(total?.totalTwoMonths || 0), "total-row-credit"),
        headerCell(formatCurrency(total?.totalThreeMonths || 0), "total-row-credit"),
        headerCell(formatCurrency(total?.totalOverThreeMonths || 0), "total-row-credit"),
      ],
    },
    {
      rowId: `${title}-Footer`,
      height: ROW_HEIGHT,
      cells: [
        headerCell(title, "total-row"),
        ...range(0, columns.length - 3).map(() => headerCell("", "total-row")),
        headerCell(
          "Total Aging", "total-row-debit"),
        headerCell(formatCurrency(Number(totalAging)) || 0, "total-row-credit"),
      ],
    },
  ];
  if (groups?.length) {
    return [
      ...groups.map((item, index) => ({
        rowId: item.id,
        height: ROW_HEIGHT,
        cells: columns.map((column) => {
          const textCellArgs = {};

          if (column.formatter) {
            textCellArgs.text = column.formatter(item[column.columnId], index);
          } else {
            textCellArgs.text = item[column.columnId] ?? "";
          }

          return nonEditable(textCell(textCellArgs), column.className || "");
        }),
      })),
      ...totalObjects
    ];
  }
  return [];
}

function getAdvancePaymentGroupRows(title, groups, columns, total) {
  const totalObjects = [{
    rowId: `${title}-Footer`,
    height: ROW_HEIGHT,
    cells: [
      headerCell(title, "total-row"),
      ...range(0, columns.length - 3).map(() => headerCell("", "total-row")),
      headerCell(
        "Total Amount", "total-row-debit"),
      headerCell(formatCurrency(total?.totalAdvancePayment || 0), "total-row-credit"),
    ],
  },
  {
    rowId: `${title}-Footer`,
    height: ROW_HEIGHT,
    cells: [
      headerCell(title, "total-row"),
      ...range(0, columns.length - 3).map(() => headerCell("", "total-row")),
      headerCell(
        "Total Recovered", "total-row-debit"),
      headerCell(formatCurrency(total?.totalRecoveredAmount || 0), "total-row-credit"),
    ],
  },
  {
    rowId: `${title}-Footer`,
    height: ROW_HEIGHT,
    cells: [
      headerCell(title, "total-row"),
      ...range(0, columns.length - 3).map(() => headerCell("", "total-row")),
      headerCell(
        "Total Remaining", "total-row-debit"),
      headerCell(formatCurrency(total?.totalRemainingAmount || 0), "total-row-credit"),
    ],
  },
  ];
  if (groups?.length) {
    return [
      ...groups.map((item, index) => ({
        rowId: item.id,
        height: ROW_HEIGHT,
        cells: columns.map((column) => {
          const textCellArgs = {};

          if (column.formatter) {
            textCellArgs.text = column.formatter(item[column.columnId], index);
          } else {
            textCellArgs.text = item[column.columnId] ?? "";
          }

          return nonEditable(textCell(textCellArgs), column.className || "");
        }),
      })),
      ...totalObjects
    ];
  }
  return [];
}

function getPaidInvoiceGroupRows(title, groups, columns, total) {
  const totalObjects = [{
    rowId: `${title}-Footer`,
    height: ROW_HEIGHT,
    cells: [
      headerCell(title, "total-row"),
      ...range(0, columns.length - 3).map(() => headerCell("", "total-row")),
      headerCell(
        "Total Amount", "total-row-debit"),
      headerCell(formatCurrency(total?.totalPaidInvoice || 0), "total-row-credit"),
    ],
  }
  ];
  if (groups?.length) {
    return [
      ...groups.map((item, index) => ({
        rowId: item.id,
        height: ROW_HEIGHT,
        cells: columns.map((column) => {
          const textCellArgs = {};

          if (column.formatter) {
            textCellArgs.text = column.formatter(item[column.columnId], index);
          } else {
            textCellArgs.text = item[column.columnId] ?? "";
          }

          return nonEditable(textCell(textCellArgs), column.className || "");
        }),
      })),
      ...totalObjects
    ];
  }
  return [];
}

// The open transactions, one section per kind of document, each with its own total -- the same shape as the
// tenant statement's invoice groups. A credit note only gets a row of its own when the invoice it points at is
// not in the statement (it is otherwise shown on that invoice's row), so it is never counted twice.
const TRANSACTION_GROUPS = [
  { key: "expense", title: "Expenses" },
  { key: "amc", title: "AMC Invoices" },
  { key: "purchase", title: "Purchase Invoices" },
  { key: "credit", title: "Credit Notes Not Against An Invoice" },
];

// The documents that still have a balance, as one list with one total. No sections: the grouping by kind of document
// belongs to the full list of invoices.
export function getSupplierOpenItemRows({
  data,
  columns
}) {
  if (!data?.data?.length) {
    return [];
  }

  const items = data.data.map((item, index) => ({
    ...item,
    id: item.id ?? `open-${item.number || "na"}-${index}`,
    total: Number(item.total) || 0,
    paidAmount: Number(item.paidAmount) || 0,
    advanceAmount: Number(item.advanceAmount) || 0,
    creditNoteAmount: Number(item.creditNoteAmount) || 0,
    balance: Number(item.balance) || 0,
  }));

  const totals = sumGroup(items, columns);

  return [
    getHeaderRow(columns),
    ...items.map((item, index) => ({
      rowId: item.id,
      height: ROW_HEIGHT,
      cells: columns.map((column) => nonEditable(
        textCell({ text: column.formatter ? column.formatter(item[column.columnId], index) : item[column.columnId] ?? "" }),
        column.className || ""
      )),
    })),
    {
      rowId: "supplier-open-items-total",
      height: ROW_HEIGHT,
      cells: columns.map((column, index) => {
        if (index === 0) {
          return headerCell("Total", "total-row");
        }

        const total = totals[column.columnId];

        return headerCell(total === undefined ? "" : formatCurrency(total), "total-row total-row-credit");
      }),
    },
  ];
}

export function getSupplierStatementRows({
  data,
  columns
}) {
  if (!data) {
    return [];
  }

  const items = (data.data || []).map((item, index) => ({
    ...item,
    // ReactGrid needs a unique rowId; a query report has no record key to offer.
    id: item.id ?? `${item.category || "other"}-${item.number || "na"}-${index}`,
    amount: Number(item.amount) || 0,
    vat: Number(item.vat) || 0,
    total: Number(item.total) || 0,
    paidAmount: Number(item.paidAmount) || 0,
    advanceAmount: Number(item.advanceAmount) || 0,
    creditNoteAmount: Number(item.creditNoteAmount) || 0,
    balance: Number(item.balance) || 0,
  }));

  if (!items.length) {
    return [];
  }

  // Anything with a category this screen does not know about is still shown, in an "Other"
  // section, rather than silently dropped from the statement.
  const known = new Set(TRANSACTION_GROUPS.map((group) => group.key));
  const grouped = TRANSACTION_GROUPS.map((group) => ({
    ...group,
    rows: items.filter((item) => item.category === group.key),
  }));
  const others = items.filter((item) => !known.has(item.category));
  if (others.length) {
    grouped.push({ key: "other", title: "Other", rows: others });
  }

  const totals = sumGroup(items, columns);

  const grandTotalRow = {
    rowId: "supplier-statement-grand-total",
    height: ROW_HEIGHT,
    cells: columns.map((column, index) => {
      if (index === 0) {
        return headerCell("Total", "total-row");
      }

      const total = totals[column.columnId];

      return headerCell(total === undefined ? "" : formatCurrency(total), "total-row total-row-credit");
    }),
  };

  return [
    getHeaderRow(columns),
    ...grouped.flatMap((group) => getGroupRows(group.title, group.rows, columns, formatCurrency)),
    grandTotalRow,
  ];
}

export function getAgingRows({
  data,
  columns
}) {
  if (!data) {
    return [];
  }

  const totalNotDue = data.data.reduce((acc, obj) => acc + +(obj.notDue || 0), 0);
  const totalOneMonth = data.data.reduce((acc, obj) => acc + +(obj.oneMonth || 0), 0);
  const totalTwoMonths = data.data.reduce((acc, obj) => acc + +(obj.twoMonth || 0), 0);
  const totalThreeMonths = data.data.reduce((acc, obj) => acc + +(obj.threeMonth || 0), 0);
  const totalOverThreeMonths = data.data.reduce((acc, obj) => acc + +(obj.overThreeMonths || 0), 0);
  const totalAging = data.data.reduce((acc, obj) => acc + +(obj.total || 0), 0);

  const total = {
    totalTwoMonths,
    totalNotDue,
    totalOneMonth,
    totalThreeMonths,
    totalOverThreeMonths
  };
  return [getHeaderRow(columns), ...getOpenTransactionGroupRows("", data.data, columns, total, "aging", totalAging),
  ];
}

export function getAdvancePaymentRows({
  data,
  columns
}) {
  if (!data) {
    return [];
  }
  const totalAdvancePayment = data.data.filter((item) => item.type === "AdvancePayment").reduce((acc, obj) => acc + +(obj.amount || 0), 0);
  const totalRecoveredAmount = data.data.filter((item) => item.type === "RecoveredAmount").reduce((acc, obj) => acc + +(obj.amount || 0), 0);
  const totalRemainingAmount = (totalAdvancePayment || 0) - (totalRecoveredAmount || 0);

  const total = {
    totalAdvancePayment,
    totalRecoveredAmount,
    totalRemainingAmount
  };
  return [getHeaderRow(columns), ...getAdvancePaymentGroupRows("", data.data, columns, total),
  ];
}

export function getSupplierPaidInvoicesRows({
  data,
  columns
}) {
  if (!data) {
    return [];
  }
  const totalPaidInvoice = data.data.reduce((acc, obj) => acc + +(obj.paid || 0), 0);

  const total = {
    totalPaidInvoice,
  };
  return [getHeaderRow(columns), ...getPaidInvoiceGroupRows("", data.data, columns, total),
  ];
}
